/**
 * AdminDashboardWrapper Component
 *
 * Wrapper component for admin views of user portfolios.
 * Provides admin-specific UI chrome (banner, position history modal)
 * while keeping the core PortfolioTable generic and reusable.
 *
 * Usage:
 * ```tsx
 * <AdminDashboardWrapper userId={userId} accountCode={accountSummary?.accountCode}>
 *   <PortfolioTable config={adminUserConfig} {...data} />
 * </AdminDashboardWrapper>
 * ```
 */

import React, { useState, useCallback, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { usePositionHistory } from "../../hooks/useAccountData";
import usersService from "../../services/usersService";
import AdminBanner from "./AdminBanner";
import PositionHistoryModal from "../PositionHistoryModal";

// ============================================================================
// Types
// ============================================================================

export interface AdminDashboardWrapperProps {
  /** The user ID being viewed (required for fetching position history) */
  userId: string;
  /** The account code for display in the banner */
  accountCode?: string;
  /** The portfolio table and any other content to wrap */
  children: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

const AdminDashboardWrapper: React.FC<AdminDashboardWrapperProps> = ({
  userId,
  accountCode,
  children,
}) => {
  const { isDarkMode } = useTheme();

  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUserName(null);
    setUserEmail(null);
    usersService
      .findUsers({ searchString: userId, searchColumn: "id", pageSize: 1 })
      .then((res) => {
        if (cancelled) return;
        const user = res.items[0];
        if (user) {
          setUserName(user.name ?? null);
          setUserEmail(user.email);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  // Position History Modal State
  const [positionHistoryModalOpen, setPositionHistoryModalOpen] = useState(false);
  const [positionHistoryLimit, setPositionHistoryLimit] = useState(100);

  // Fetch position history data
  const {
    data: positionHistoryData,
    isLoading: positionHistoryLoading,
  } = usePositionHistory(userId, positionHistoryLimit);

  const positionHistory = positionHistoryData?.history || [];
  const positionHistoryCount = positionHistoryData?.count || 0;

  // Handlers
  const handleOpenPositionHistory = useCallback(() => {
    setPositionHistoryModalOpen(true);
  }, []);

  const handleClosePositionHistory = useCallback(() => {
    setPositionHistoryModalOpen(false);
  }, []);

  const handleLoadMoreHistory = useCallback((newLimit: number) => {
    setPositionHistoryLimit(newLimit);
  }, []);

  return (
    <div className={`h-full flex flex-col ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
      {/* Admin Banner */}
      <AdminBanner
        accountCode={accountCode}
        userId={userId}
        userName={userName}
        userEmail={userEmail}
        onOpenPositionHistory={handleOpenPositionHistory}
      />

      {/* Main Content (PortfolioTable) */}
      <div className="flex-1 overflow-hidden">
        {children}
      </div>

      {/* Position History Modal */}
      <PositionHistoryModal
        isOpen={positionHistoryModalOpen}
        onClose={handleClosePositionHistory}
        isDarkMode={isDarkMode}
        positionHistory={positionHistory}
        positionHistoryCount={positionHistoryCount}
        positionHistoryLoading={positionHistoryLoading}
        onLoadMore={handleLoadMoreHistory}
      />
    </div>
  );
};

export default AdminDashboardWrapper;

